import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { AppBar, Tab, Icon, Menu, MenuItem, Typography } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import TabContainer from 'global/components/TabContainer';
import ReportAssessmentInfo from '../AssessmentInfo';
import ReportAssessmentScoreContent from '../AssessmentScore';
import Highlights from '../Highlights';
import ReportAssessmentPhotos from '../Assessment/ReportAssessmentPhotos';
import { get, size } from 'global/lib/lodash';
import Image from 'global/components/image';
import {
  TabsWrapper,
  StyledTabs,
  HideMobileSpan,
  OnlyMobileSpan,
  tabClassLookup,
  PrintHeader,
  PrintHeaderTitles,
  PrintHeaderLogo,
} from './ReportTabs.styles';
import { PrintView } from 'global/components/print';
import SummaryScores from '../SummaryScores';
import MultiAssessment from '../AssessmentScore/MultiAssessment';
import TasksPrintSection from '../TasksPrintSection';
import { _size } from 'global/utils';
import { useScreen } from 'global/styles';
import { getAccountSetting } from 'redux/actions/userLogin';
import { SETTINGS } from 'feature/admin/account-settings';
import { ASSESSMENT_HEADER_ACTIONS_SLOT_ID } from 'feature/nav/NavBar/NavBar.styles';
// PFM-2547: useHashView no longer gates the Print/Download cluster — only the
// Share button (in ReportAssessment) is hidden for hashed viewers; Print and
// Download must remain available so recipients can take the data with them.

const tabs = [
  {
    value: 'info',
    label: 'Info',
    Component: ReportAssessmentInfo,
  },
  {
    value: 'scores',
    label: 'Scores',
    Component: ReportAssessmentScoreContent,
    className: 'noPad',
    canShow: (props) => {
      const { assessment } = props;
      const status = get(assessment, 'data.status');
      const scores = get(assessment, 'data.scores') || {};
      const loading = assessment.loading;
      if (loading) {
        return false;
      }

      const nascores = Object.keys(scores).filter((k) => {
        const { calculatedScore } = scores[k];
        return !calculatedScore;
      });

      let hasScores = _size(nascores) < _size(scores);

      if (!hasScores && (status === 'review' || status === 'published')) {
        return false;
      }

      return true;
    },
  },
  {
    value: 'highlights',
    label: 'Highlights',
    Component: Highlights,
    canShow: (props) => {
      const { assessment, highlights, isTemplate, printView } = props;
      const hasHighlights = size(highlights.data);
      const status = get(assessment, 'data.status');
      const isLot = get(assessment, 'data.realm.tier') === 'lot';
      const loading = assessment.loading;
      if (loading) {
        return false;
      }

      if (isTemplate) {
        return false;
      }

      if (isLot) {
        return false;
      } else if (!hasHighlights && (status === 'review' || status === 'published' || printView)) {
        return false;
      }

      return true;
    },
  },
  {
    value: 'photos',
    label: 'Photos',
    Component: ReportAssessmentPhotos,
    canShow: (props) => {
      const { assessment, isTemplate } = props;
      const status = get(assessment, 'data.status');
      const photosCount = get(assessment, 'data.realmPhotoCount', 0);
      const loading = assessment.loading;
      if (loading) {
        return false;
      }

      let hasPhotos = photosCount > 0;

      if (isTemplate) {
        return false;
      }

      if (!hasPhotos && (status === 'review' || status === 'published')) {
        return false;
      }

      return true;
    },
  },
];

// Map section names to their print tab definitions
const SECTION_MAP = {
  info: { ...tabs[0], sectionLabel: 'Info' },
  score_summary: {
    Component: SummaryScores,
    pagebreak: false,
    sectionLabel: 'Score Summary',
    canShow: () => true,
  },
  highlights: { ...tabs[2], sectionLabel: 'Highlights' },
  scores: { Component: MultiAssessment, sectionLabel: 'Scores' },
  tasks: {
    Component: TasksPrintSection,
    sectionLabel: 'Tasks',
    canShow: (props) => {
      const { tasks, isTemplate, printView } = props;
      if (!printView) return false;
      if (isTemplate) return false;
      if (get(tasks, 'loading')) return false;
      return size(get(tasks, 'data')) > 0;
    },
  },
};

const DEFAULT_PRINT_OPTIONS = [
  {
    name: 'Print Report',
    tier: [],
    sections: [
      { name: 'info', order: 1, enabled: true },
      { name: 'score_summary', order: 2, enabled: true },
      { name: 'highlights', order: 3, enabled: true },
      { name: 'scores', order: 4, enabled: true },
      { name: 'tasks', order: 5, enabled: false },
    ],
  },
];

const getApplicablePrintOptions = (assessmentTier) => {
  const options = getAccountSetting(SETTINGS.assessmentPrintOptions) || DEFAULT_PRINT_OPTIONS;
  if (!Array.isArray(options) || !size(options)) return DEFAULT_PRINT_OPTIONS;

  const filtered = options.filter((opt) => {
    const tiers = get(opt, 'tier', []);
    if (!size(tiers)) return true; // empty tier = applies to all
    return tiers.includes(assessmentTier);
  });

  return size(filtered) ? filtered : DEFAULT_PRINT_OPTIONS;
};

const buildPrintTabs = (sections, allProps) => {
  const enabledSections = [...sections].filter((s) => s.enabled !== false).sort((a, b) => a.order - b.order);

  return enabledSections
    .map((section) => {
      const tabDef = SECTION_MAP[section.name];
      if (!tabDef) return null;
      return tabDef;
    })
    .filter((t) => t && (t.canShow ? t.canShow({ printView: true, ...allProps }) : true));
};

export default function ReportTabs(props) {
  const { page, currentTabValue, assessment, canShowScores, shareActions } = props;
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activePrintOption, setActivePrintOption] = useState(null);
  const [sectionToggles, setSectionToggles] = useState(null);
  const [openTrigger, setOpenTrigger] = useState(0);

  const loading = get(assessment, 'loading', false);
  const assessmentTier = get(assessment, 'data.realm.tier', '');
  const accountSettings = useSelector((state) => get(state, 'auth.accountSettings'));
  const builderLogo = useSelector((state) => get(state, 'view.style.props.logo'));

  const printOptions = useMemo(() => getApplicablePrintOptions(assessmentTier), [assessmentTier, accountSettings]);
  const hasMultipleOptions = size(printOptions) > 1;

  // Determine which print option is active (default to first)
  const currentOption = activePrintOption || get(printOptions, '0');
  const currentSections = get(currentOption, 'sections', []);

  // Apply user toggles on top of the default sections
  const effectiveSections = useMemo(() => {
    if (!sectionToggles) return currentSections;
    return currentSections.map((s) => ({
      ...s,
      enabled: sectionToggles[s.name] !== undefined ? sectionToggles[s.name] : s.enabled !== false,
    }));
  }, [currentSections, sectionToggles]);

  const currentPrintTabs = useMemo(() => buildPrintTabs(effectiveSections, props), [effectiveSections, props]);
  const scoresTab = tabs.find((tab) => tab.value === 'scores');
  const scoresCanShow = scoresTab?.canShow ? scoresTab.canShow(props) : true;
  const activeTabValue = currentTabValue === 'scores' && !scoresCanShow ? 'info' : currentTabValue;

  const onSelectPrintOption = (opt) => {
    setActivePrintOption(opt);
    setSectionToggles(null);
    setMenuAnchor(null);
    setOpenTrigger((t) => t + 1);
  };

  const handlePrintClick = (e) => {
    if (hasMultipleOptions) {
      setMenuAnchor(e.currentTarget);
    }
  };

  const printBtnText = (
    <>
      <OnlyMobileSpan>
        <PrintIcon />
      </OnlyMobileSpan>
      <HideMobileSpan>Print{hasMultipleOptions ? ' \u25BE' : ''}</HideMobileSpan>
    </>
  );

  // Build section toggle config for PrintView
  const sectionToggleConfig = currentSections
    .map((s) => {
      const tabDef = SECTION_MAP[s.name];
      if (!tabDef) return null;
      const hasContent = tabDef.canShow ? tabDef.canShow({ printView: true, ...props }) : true;
      if (!hasContent) return null;
      const isEnabled = sectionToggles ? (sectionToggles[s.name] !== undefined ? sectionToggles[s.name] : s.enabled !== false) : s.enabled !== false;
      return { name: s.name, label: get(tabDef, 'sectionLabel', s.name), enabled: isEnabled };
    })
    .filter(Boolean);

  const onToggleSection = (sectionName, enabled) => {
    setSectionToggles((prev) => ({ ...(prev || {}), [sectionName]: enabled }));
  };

  // PFM-2628: the Share/Download/Print cluster is rendered from a single shared
  // config. The in-page instance (inside the tabs AppBar) keeps the desktop
  // top-right overlay but is HIDDEN below `md`. A second instance is portaled
  // into the assessment header slot and is VISIBLE only below `md`, rendering as
  // a full-width row under the title (above the Location/Status row). This keeps
  // all print/share/export logic in one component and avoids duplicate buttons
  // showing at the same breakpoint.
  const printViewProps = {
    items: currentPrintTabs.map((tab, i) => {
      const assessmentName = get(props, 'assessment.data.name', '');
      return {
        title:
          i === 0 ? (
            <PrintHeader>
              <PrintHeaderTitles>
                <Typography variant="h2" style={{ fontWeight: 'bold' }}>
                  {assessmentName}
                </Typography>
                {hasMultipleOptions && get(currentOption, 'name') && <Typography variant="subtitle1">{currentOption.name}</Typography>}
              </PrintHeaderTitles>
              {builderLogo && (
                <PrintHeaderLogo>
                  <Image style={{ height: 100 }} src={builderLogo} alt="Builder Logo" unsetWidth />
                </PrintHeaderLogo>
              )}
            </PrintHeader>
          ) : undefined,
        render: (rprops) => <tab.Component {...props} {...(rprops || {})} printView={true} />,
      };
    }),
    assessment: props.assessment.data,
    useLoaded: true,
    hideCoverPage: true,
    buttonContainerStyle: {},
    onOpen: () => get(props, 'scores.update', () => 0)(),
    printBtn: {
      text: printBtnText,
      onClick: hasMultipleOptions ? handlePrintClick : undefined,
    },
    exportBtn: {
      disabled: !canShowScores,
      text: (
        <>
          <OnlyMobileSpan>
            <Icon>insert_chart</Icon>
          </OnlyMobileSpan>
          <HideMobileSpan>Download Data</HideMobileSpan>
        </>
      ),
    },
    shareBtn: shareActions,
    sectionToggles: sectionToggleConfig,
    onToggleSection,
  };

  // Only one cluster is visible per breakpoint (mobile header vs desktop
  // overlay). Route the multi-option print `openTrigger` to the visible
  // instance so picking an option doesn't open both PrintView dialogs at once.
  const { isDown: isMobile } = useScreen('md');

  // Resolve the header portal target after mount — the slot div lives in the
  // sibling NavBarContent header, so it isn't in the DOM during this component's
  // first render pass.
  const [headerActionsSlot, setHeaderActionsSlot] = useState(null);
  useEffect(() => {
    setHeaderActionsSlot(document.getElementById(ASSESSMENT_HEADER_ACTIONS_SLOT_ID));
  }, []);

  return (
    <TabsWrapper>
      <AppBar position="static">
        <StyledTabs value={loading ? 'info' : activeTabValue} variant="scrollable" textColor="inherit" aria-label="scrollable auto tabs">
          {tabs.map((item, index) => {
            const canShow = item.canShow ? item.canShow(props) : true;
            if (!canShow) {
              return null;
            }
            return <Tab component={Link} to={`${page}/${item.value}`} label={item.label} value={item.value} key={item.value} aria-label={item.value} />;
          })}
        </StyledTabs>
        {/* In-page cluster: desktop top-right overlay (md+); hidden below md. */}
        <PrintView {...printViewProps} mobileVariant="hideOnMobile" openTrigger={isMobile ? 0 : openTrigger} />
        {/* Mobile cluster: portaled under the assessment title; visible only below md. */}
        {headerActionsSlot && createPortal(<PrintView {...printViewProps} mobileVariant="headerRow" openTrigger={isMobile ? openTrigger : 0} />, headerActionsSlot)}
        {hasMultipleOptions && (
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
            {printOptions.map((opt, i) => (
              <MenuItem key={i} onClick={() => onSelectPrintOption(opt)} selected={opt === currentOption}>
                {opt.name}
              </MenuItem>
            ))}
          </Menu>
        )}
      </AppBar>
      {tabs.map((item) => {
        const TabComponent = item.Component;
        const className = tabClassLookup[get(item, 'className')];
        return (
          activeTabValue === item.value && (
            <TabContainer className={className} key={item.value}>
              <TabComponent {...props} />
            </TabContainer>
          )
        );
      })}
    </TabsWrapper>
  );
}
